import type * as hast from 'hast';
import type { Plugin } from 'unified';

import { BLOCK_PARAGRAPH, type DocumentSchemaType, normalizeDocument } from '@gitbook/doc-core';
import { Block, Document, type Inline, Text } from '@gitbook/slate';

import type { ParsingContext } from '../context';
import { parseFootnotes } from './parseFootnotes';
import { mergeEmptyFootnotes } from './plugin-mergeEmptyFootnotes';
import { normalizeHtmlTree } from './plugin-normalizeTree';
import { stripEmptyAnchor } from './plugin-stripEmptyAnchor';
import { transformBlockCodes } from './transform-block-codes';
import { defaultCustomBlockTransform, transformBlockCustoms } from './transform-block-customs';
import { transformBlockExpandable } from './transform-block-expandable';
import { transformBlockLeafs } from './transform-block-leafs';
import { transformBlockLists } from './transform-block-lists';
import { transformBlockQuotes } from './transform-block-quotes';
import { transformBlockTables } from './transform-block-tables';
import { transformFigures } from './transform-figures-pictures';
import { transformImages } from './transform-images';
import { transformImagesOrMaths } from './transform-images-maths';
import { transformInlineCustoms } from './transform-inline-customs';
import { transformInlineLinks } from './transform-inline-links';
import { transformMarks } from './transform-marks';
import { transformTextNode } from './transform-text';
import type {
    HastToGastEatNodesFn,
    HastToGastTransformContext,
    HastToGastTransformMap,
} from './types';
import { isElementCustomNode } from './utils';

const transformsByTagName: HastToGastTransformMap = {
    ...transformBlockLeafs,
    ...transformBlockLists,
    ...transformBlockCodes,
    ...transformBlockQuotes,
    ...transformInlineLinks,
    ...transformFigures,
    ...transformImages,
    ...transformBlockTables,
    ...transformBlockExpandable,
    ...transformImagesOrMaths,
    ...transformMarks,
};

export interface HastToGastResult {
    document: Document;
    attributes: { [key: string]: string };
}

/**
 * Unified compiler to transform a rehype (HTML) AST into a GitBook one.
 */
export const hastToGast: Plugin<
    [ParsingContext],
    hast.Root,
    Promise<HastToGastResult>
> = function hastToGast(parsingContext) {
    const documentSchema: DocumentSchemaType = parsingContext.documentSchemaType ?? 'document';

    this.use(normalizeHtmlTree);
    this.use(mergeEmptyFootnotes);
    this.use(stripEmptyAnchor);

    const compiler = async (tree) => {
        const initialContext: HastToGastTransformContext = {
            attributes: {},
            marks: [],
            parseImagesAsBlocks: false,
        };

        async function transformNodesWithParent(
            parent: hast.Root | hast.Element,
            children: hast.Element['children'] | hast.Root['children'],
            context: HastToGastTransformContext
        ): Promise<Array<Block | Inline | Text>> {
            const result: Array<Block | Inline | Text> = [];

            for (let index = 0; index < children.length; index++) {
                const child = children[index];

                if (child.type === 'doctype' || child.type === 'comment') {
                    continue;
                }

                // @ts-expect-error
                const transformed = await transformNode(child, parent, index, context);

                if (!transformed.length) {
                    continue;
                }

                result.push(...transformed);
            }

            return result;
        }

        async function transformNode(
            node: hast.Element | hast.Text,
            parent: hast.Root | hast.Element,
            index: number,
            context: HastToGastTransformContext
        ): Promise<(Text | Inline | Block)[]> {
            // @ts-expect-error
            const eat: HastToGastEatNodesFn = async (mode, children, context, eatParent) => {
                const nodes = await transformNodesWithParent(eatParent || node, children, context);
                return normalizeNodes(mode, nodes);
            };

            let result: Array<Block | Inline | Text> | void;

            if (node.type === 'text') {
                // @ts-expect-error
                result = await transformTextNode(node, eat, context, parsingContext, parent, index);
                if (!result) {
                    return [];
                }
                return result;
            }

            if (isElementCustomNode(node)) {
                const customTagName = node.properties?.dataTag;
                if (typeof customTagName === 'string') {
                    const transformByCustomTagName =
                        node.tagName === 'div'
                            ? transformBlockCustoms[customTagName] || defaultCustomBlockTransform
                            : transformInlineCustoms[customTagName];
                    if (transformByCustomTagName) {
                        result = await transformByCustomTagName(
                            node,
                            eat,
                            context,
                            parsingContext,
                            parent,
                            index
                        );
                        if (result) {
                            return result;
                        }
                    }
                }
            }

            const transformByTagName = transformsByTagName[node.tagName];
            if (transformByTagName) {
                result = await transformByTagName(
                    node,
                    eat,
                    context,
                    parsingContext,
                    parent,
                    index
                );
                if (result) {
                    return result;
                }
            }

            if (Array.isArray(node.children)) {
                // If there are no handlers for this node, we process the children
                return transformNodesWithParent(node, node.children, context);
            }

            return [];
        }

        // First lookup and index the footnote definitions
        // it also removes them from the tree.
        await parseFootnotes(tree, parsingContext, (node) =>
            // @ts-expect-error
            transformNodesWithParent(node, node.children, initialContext)
        );

        const nodes = await transformNodesWithParent(tree, tree.children, initialContext);
        const data = documentSchema !== 'document' ? { schemaType: documentSchema } : {};
        const document = normalizeDocument(
            Document.create({
                data,
                nodes: wrapInlinesAndTexts(nodes),
            }),
            documentSchema
        );

        return { document, attributes: initialContext.attributes };
    };

    // @ts-expect-error
    this.Compiler = compiler;
};

/**
 * Wrap all inline and text nodes in paragraphs
 */
function wrapInlinesAndTexts(nodes: (Block | Inline | Text)[]): Block[] {
    const resultingNodes: Block[] = [];
    let inlineNodes: (Text | Inline)[] = [];
    nodes.forEach((node) => {
        if (node.isInline() || node.isText()) {
            inlineNodes.push(node);
        } else {
            if (inlineNodes.length) {
                resultingNodes.push(
                    Block.create({
                        type: BLOCK_PARAGRAPH,
                        nodes: normalizeInlinesAndText(inlineNodes),
                    })
                );
                inlineNodes = [];
            }
            resultingNodes.push(node);
        }
    });
    if (inlineNodes.length) {
        resultingNodes.push(
            Block.create({
                type: BLOCK_PARAGRAPH,
                nodes: normalizeInlinesAndText(inlineNodes),
            })
        );
        inlineNodes = [];
    }

    return resultingNodes;
}

/**
 * Normalize nodes to be only blocks or only inlines.
 */
function normalizeNodes(mode: 'inlines', nodes: Array<Block | Inline | Text>): Array<Inline | Text>;
function normalizeNodes(
    mode: string,
    nodes: Array<Block | Inline | Text>
): Array<Block | Inline | Text> {
    let stackedInlines: Array<Inline | Text> = [];
    const result: Array<Block | Inline | Text> = [];

    nodes.forEach((node) => {
        if (mode === 'inlines') {
            if (node.object === 'block') {
                result.push(...normalizeNodes(mode, node.nodes.toArray()));
            } else {
                result.push(node);
            }
        } else {
            if (node.object === 'inline' || node.object === 'text') {
                stackedInlines.push(node);
            } else {
                if (stackedInlines.length) {
                    result.push(...wrapNodes(stackedInlines));
                }
                result.push(node);
                stackedInlines = [];
            }
        }
    });

    if (stackedInlines.length) {
        result.push(...wrapNodes(stackedInlines));
    }

    // @ts-expect-error
    return mode === 'inlines' ? normalizeInlinesAndText(result) : result;
}

/**
 * Wrap the given nodes in a paragraph.
 */
function wrapNodes(nodes: (Inline | Text)[]): Block[] {
    return [
        Block.create({
            type: BLOCK_PARAGRAPH,
            nodes: normalizeInlinesAndText(nodes),
        }),
    ];
}

/**
 * Surround the given nodes to ensure that we create a Text entry
 * at the start and at the end so that the parent doesn't require normalization from core-schema.
 * It also takes care of merging consecutive Text nodes in place to prevent other core-schema normalization.
 */
function normalizeInlinesAndText(nodes: (Inline | Text)[]): (Inline | Text)[] {
    if (nodes.length === 0) {
        return [];
    }

    const result: (Inline | Text)[] = [];

    // Concatenate sibling text nodes
    for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        if (node.object !== 'text') {
            result.push(node);
            continue;
        }

        let next = nodes[i + 1];
        if (!next || next.object !== 'text') {
            result.push(node);
            continue;
        }

        let mergedNodes: Text = node;
        while (next && next.object === 'text') {
            mergedNodes = mergedNodes.mergeText(next);
            i++;
            next = nodes[i + 1];
        }

        result.push(mergedNodes);
    }

    // Ensure inline nodes are surrounded between text nodes
    return [
        ...(result[0].object !== 'text' ? [Text.create()] : []),
        ...result,
        ...(result[result.length - 1].object !== 'text' ? [Text.create()] : []),
    ];
}
